'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { coachService } from '@/services/coach';
import type { CoachAvailability, CoachAvailabilityException } from '@court-sync/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { Button } from '@/components/ui';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';

function formatExceptionDate(val: string | Date | undefined): string {
  if (!val) return '';
  const d = typeof val === 'string' ? new Date(val) : val;
  return format(d, 'MMM d, yyyy');
}

function normalizeDateForCompare(val: string | Date | undefined): string {
  if (!val) return '';
  const d = typeof val === 'string' ? new Date(val) : val;
  return format(d, 'yyyy-MM-dd');
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// 6am to 10pm = 32 half-hour slots (6:00 through 21:30, last slot ends 22:00)
const TIME_SLOTS = Array.from({ length: 32 }, (_, i) => {
  const hour = 6 + Math.floor(i / 2);
  const minute = i % 2 === 0 ? '00' : '30';
  return `${String(hour).padStart(2, '0')}:${minute}`;
});

export type CoachAvailabilityEditorProps = {
  coachId: string;
  academyId: string;
  /** `page` = full-width grid; `embedded` = scrollable for use inside another dialog */
  layout?: 'page' | 'embedded';
  /** Coach editing own schedule vs academy owner managing a coach */
  audience?: 'self' | 'ownerManagingCoach';
  /** Called after a successful write. `source` distinguishes weekly save vs exception changes. */
  onSaved?: (detail: { source: 'weekly' | 'exception' | 'exceptionDeleted' }) => void;
};

export function CoachAvailabilityEditor({
  coachId,
  academyId,
  layout = 'page',
  audience = 'self',
  onSaved,
}: CoachAvailabilityEditorProps) {
  const [_availability, setAvailability] = useState<CoachAvailability[]>([]);
  const [exceptions, setExceptions] = useState<CoachAvailabilityException[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const [exceptionDateFrom, setExceptionDateFrom] = useState('');
  const [exceptionDateTo, setExceptionDateTo] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const isDraggingRef = useRef(false);

  const isSelf = audience === 'self';

  const loadData = useCallback(async () => {
    if (!academyId || !coachId) return;
    try {
      setLoading(true);
      setError(null);
      const [avail, excepts] = await Promise.all([
        coachService.getAvailability(coachId, academyId),
        coachService.getAvailabilityExceptions(coachId, academyId),
      ]);
      setAvailability(avail);
      setExceptions(excepts);
      initializeSelectedSlots(avail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load availability');
    } finally {
      setLoading(false);
    }
  }, [academyId, coachId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const initializeSelectedSlots = (avail: CoachAvailability[]) => {
    if (avail.length === 0) {
      const allSlots = new Set<string>();
      DAYS.forEach((_, dayIdx) => {
        TIME_SLOTS.slice(0, -1).forEach((time, timeIdx) => {
          const nextTime = TIME_SLOTS[timeIdx + 1];
          allSlots.add(`${dayIdx}-${time}-${nextTime}`);
        });
      });
      setSelectedSlots(allSlots);
      return;
    }
    const slots = new Set<string>();
    avail.forEach((a) => {
      if (a.isAvailable) {
        slots.add(`${a.dayOfWeek}-${a.startTime}-${a.endTime}`);
      }
    });
    setSelectedSlots(slots);
  };

  const toggleSlot = (dayOfWeek: number, time: string) => {
    const nextTime = TIME_SLOTS[TIME_SLOTS.indexOf(time) + 1] || '22:00';
    const key = `${dayOfWeek}-${time}-${nextTime}`;
    const newSlots = new Set(selectedSlots);
    if (newSlots.has(key)) {
      newSlots.delete(key);
    } else {
      newSlots.add(key);
    }
    setSelectedSlots(newSlots);
  };

  const handleSaveAvailability = async () => {
    if (!academyId || !coachId) return;
    try {
      setSaving(true);
      setError(null);
      const slots = Array.from(selectedSlots).map((key) => {
        const [dayOfWeek, startTime, endTime] = key.split('-');
        return {
          dayOfWeek: parseInt(dayOfWeek, 10),
          startTime,
          endTime,
          isAvailable: true,
        };
      });
      await coachService.updateAvailability(coachId, academyId, slots);
      setAvailability(slots as CoachAvailability[]);
      toast.success('Availability saved');
      onSaved?.({ source: 'weekly' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save availability';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddException = async () => {
    if (!academyId || !coachId) return;
    if (!exceptionDateFrom) {
      setError('Please select a date');
      return;
    }
    const dateTo = exceptionDateTo || exceptionDateFrom;
    if (exceptionDateTo && exceptionDateTo < exceptionDateFrom) {
      setError('Date To must be on or after Date From');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const reason = exceptionReason || 'Not available';
      const result = await coachService.addAvailabilityException(coachId, {
        academyId,
        dateFrom: exceptionDateFrom,
        dateTo,
        isAvailable: false,
        reason,
      });
      const newExc: CoachAvailabilityException = {
        id: result.id,
        coachId,
        academyId,
        dateFrom: exceptionDateFrom,
        dateTo,
        isAvailable: false,
        reason,
      } as CoachAvailabilityException;
      setExceptions([...exceptions, newExc]);
      setExceptionDateFrom('');
      setExceptionDateTo('');
      setExceptionReason('');
      setExceptionDialogOpen(false);
      onSaved?.({ source: 'exception' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add exception');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteException = async (exceptionId: string) => {
    if (!coachId) return;
    try {
      setSaving(true);
      await coachService.deleteAvailabilityException(coachId, exceptionId);
      setExceptions(exceptions.filter((e) => e.id !== exceptionId));
      onSaved?.({ source: 'exceptionDeleted' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete exception');
    } finally {
      setSaving(false);
    }
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const futureExceptions = exceptions.filter((exc) => normalizeDateForCompare(exc.dateTo) >= today);

  const gridIntro = isSelf
    ? 'Click or drag over time slots to toggle your availability'
    : 'Click or drag over time slots to set when this coach is available';

  const exceptionCardDescription = isSelf
    ? "Mark dates or ranges when you won't be available (e.g. vacation, conference). Past exceptions are hidden."
    : "Mark dates or ranges when this coach won't be available. Past exceptions are hidden.";

  const exceptionDialogDescription = isSelf
    ? 'Mark dates when you won\'t be available. Leave "Date To" empty for a single day.'
    : 'Mark dates when this coach won\'t be available. Leave "Date To" empty for a single day.';

  const outerClass =
    layout === 'embedded'
      ? 'max-h-[min(70vh,36rem)] overflow-y-auto pr-1 -mr-1'
      : '';

  return (
    <div className={outerClass}>
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Weekly Schedule</CardTitle>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{gridIntro}</p>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400" role="status" aria-live="polite">
                <span className="font-medium text-gray-700 dark:text-gray-300">Click or drag to toggle availability.</span>{' '}
                <span className="inline-block w-3 h-3 rounded-sm bg-green-500 dark:bg-green-600 mr-1.5 align-middle" aria-hidden="true" />
                Available ·{' '}
                <span className="inline-block w-3 h-3 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 mr-1.5 align-middle" aria-hidden="true" />
                Not available
              </p>
              <div className="overflow-x-auto -mx-1 select-none" onMouseUp={() => { isDraggingRef.current = false; }}>
                <div
                  className="inline-grid gap-2 min-w-0 select-none"
                  style={{
                    gridTemplateColumns: `minmax(5rem, auto) repeat(${TIME_SLOTS.length - 1}, minmax(2.25rem, 2.25rem))`,
                  }}
                >
                  <div className="font-semibold text-sm text-gray-700 dark:text-gray-300 py-2 sticky left-0 bg-inherit z-10" />
                  {TIME_SLOTS.slice(0, -1).map((time) => (
                    <div
                      key={time}
                      className="font-medium text-xs text-center text-gray-600 dark:text-gray-400 py-2 whitespace-nowrap"
                    >
                      {time}
                    </div>
                  ))}

                  {DAYS.map((_, dayIdx) => (
                    <div key={dayIdx} className="contents">
                      <div className="font-medium text-sm text-gray-700 dark:text-gray-300 py-2 sticky left-0 bg-inherit z-10 flex items-center">
                        {DAYS[dayIdx].slice(0, 3)}
                      </div>
                      {TIME_SLOTS.slice(0, -1).map((time, timeIdx) => {
                        const nextTime = TIME_SLOTS[timeIdx + 1];
                        const slotKey = `${dayIdx}-${time}-${nextTime}`;
                        const isSelected = selectedSlots.has(slotKey);
                        return (
                          <button
                            key={slotKey}
                            type="button"
                            onMouseDown={(e) => {
                              if (e.button === 0) {
                                e.preventDefault();
                                isDraggingRef.current = true;
                                toggleSlot(dayIdx, time);
                              }
                            }}
                            onMouseEnter={() => {
                              if (isDraggingRef.current) {
                                toggleSlot(dayIdx, time);
                              }
                            }}
                            aria-pressed={isSelected}
                            aria-label={`${DAYS[dayIdx]} ${time}–${nextTime}: ${isSelected ? 'Available' : 'Not available'}. Click to toggle.`}
                            className={`min-w-[36px] min-h-[36px] w-9 h-9 rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                              isSelected
                                ? 'bg-green-500 dark:bg-green-600 border-green-600 dark:border-green-700 hover:bg-green-600 dark:hover:bg-green-700'
                                : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex gap-2">
                <Button
                  onClick={handleSaveAvailability}
                  disabled={saving || loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {saving ? 'Saving...' : 'Save Availability'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Date Exceptions</CardTitle>
              <CardDescription>{exceptionCardDescription}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Dialog open={exceptionDialogOpen} onOpenChange={setExceptionDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Exception
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Date Exception</DialogTitle>
                    <DialogDescription>{exceptionDialogDescription}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="exception-date-from" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date From (required)
                      </label>
                      <input
                        id="exception-date-from"
                        type="date"
                        value={exceptionDateFrom}
                        onChange={(e) => setExceptionDateFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="exception-date-to" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date To (optional)
                      </label>
                      <input
                        id="exception-date-to"
                        type="date"
                        value={exceptionDateTo}
                        onChange={(e) => setExceptionDateTo(e.target.value)}
                        min={exceptionDateFrom || undefined}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Leave empty for a single day</p>
                    </div>
                    <div>
                      <label htmlFor="exception-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reason (optional)
                      </label>
                      <input
                        id="exception-reason"
                        type="text"
                        value={exceptionReason}
                        onChange={(e) => setExceptionReason(e.target.value)}
                        placeholder="e.g., Conference, Time off"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                    <Button
                      onClick={handleAddException}
                      disabled={saving}
                      className="w-full"
                    >
                      {saving ? 'Adding...' : 'Add Exception'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {futureExceptions.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">No upcoming exceptions</p>
                ) : (
                  futureExceptions.map((exc) => (
                    <div
                      key={exc.id}
                      className="p-2 rounded bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-between"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {normalizeDateForCompare(exc.dateFrom) === normalizeDateForCompare(exc.dateTo)
                            ? formatExceptionDate(exc.dateFrom)
                            : `${formatExceptionDate(exc.dateFrom)} – ${formatExceptionDate(exc.dateTo)}`}
                        </p>
                        {exc.reason && <p className="text-xs text-gray-600 dark:text-gray-400">{exc.reason}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteException(exc.id)}
                        disabled={saving}
                        aria-label="Delete exception"
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
